import React, { useState, useEffect, useRef } from 'react';
import { Modal, TextField, BlockStack, Text, Icon, OptionList, Spinner, Listbox, Button } from '@shopify/polaris';
import { SearchIcon, PlusCircleIcon } from '@shopify/polaris-icons';
import { FetchCustom } from './FetchCustom';
import { useHostNavigate } from "../hooks";

export function CustomerTagPickerCustom(props) {
    const navigate = useHostNavigate();
    const fetch = FetchCustom();

    const [query, setQuery] = useState(props.initialQuery || '');
    const [loading, setLoading] = useState(true);
    const [list, setList] = useState([]);
    const [toAdd, setToAdd] = useState([]);
    const [available, setAvailable] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [redirectUrl, setRedirectUrl] = useState('');

    const typingTimer = useRef();
    const selectedRef = useRef([]);
    const pendingQueryRef = useRef('');

    useEffect(() => {
        selectedRef.current = selectedItems;
    }, [selectedItems]);

    useEffect(() => {
        return () => clearTimeout(typingTimer.current);
    }, []);

    const filter = (arr, query) => {
        const q = (query || '').toLowerCase();
        return arr.filter(item => item.toLowerCase().includes(q));
    };

    const runFilter = (query) => {
        setToAdd(filter(selectedRef.current, query));
        setAvailable(
            filter(
                list.filter(item => !selectedRef.current.includes(item)),
                query
            )
        );
    };

    const load = async (startCursor, endCursor, query) => {
        //const data = await fetch('api/home/customerTags', { startCursor: startCursor, endCursor: endCursor, query: query });
        const data = await fetch('api/home/customerTags', {});
        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            let sItems = props.selectedCustomerTags ? props.selectedCustomerTags : [];
            setList(data.items);
            setSelectedItems(sItems);
            setToAdd(filter(sItems, query));
            setAvailable(filter(data.items.filter(item => !sItems.includes(item)), query));
            setLoading(false);
        }
    };

    const reset = function () {
        props.handleCustomerTagPicker('open', false);
        setList([]);
        setToAdd([]);
        setAvailable([]);
        setSelectedItems([]);
        setQuery('');
        setLoading(true);
        pendingQueryRef.current = '';
    };

    useEffect(() => {
        if (props.open) {
            load(null, null, query);
        }
        // eslint-disable-next-line
    }, [props.open]);

    useEffect(() => {
        if (!loading && pendingQueryRef.current) {
            runFilter(pendingQueryRef.current);
        }
        // eslint-disable-next-line
    }, [loading]);

    useEffect(() => {
        if (redirectUrl) {
            navigate(redirectUrl);
        }
    }, [redirectUrl, navigate]);

    return (
        <Modal
            title={props.editing ? "Edit customer tags" : "Add customer tags"}
            open={props.open}
            limitHeight={true}
            onClose={() => {
                reset();
            }}
            primaryAction={{
                content: props.editing ? "Done" : "Add",
                onAction: () => {
                    props.handleChange('customerTags', selectedItems.join());
                    props.handleChange('selectedCustomerTags', selectedItems);
                    props.handleChange('segments', null);
                    props.handleChange('selectedSegments', null);
                    props.handleChange('customers', null);
                    props.handleChange('selectedCustomers', null);
                    props.handleError('customerType', !selectedItems.length);
                    reset();
                }
            }}
            secondaryActions={[
                {
                    content: 'Cancel',
                    onAction: () => {
                        reset();
                    }
                },
            ]}
        >
            <div className="customer-tag-picker">
                <div className="modal-sticky-bar">
                    <Modal.Section>
                        <BlockStack gap="300">
                            <div><Text as="span" tone="subdued">You can add some tags to customers from the</Text> <a href={props.user.shopifyAdminUrl + "/customers"} target="_blank" rel="noreferrer" className="Polaris-Link">Customers Page</a></div>
                            <TextField
                                value={query}
                                focused={true}
                                placeholder={'Search to find or add customer tags'}
                                prefix={<Icon source={SearchIcon} tone="subdued" />}
                                onChange={(newValue) => {
                                    setQuery(newValue);
                                    props.handleCustomerTagPicker('initialQuery', newValue);
                                    clearTimeout(typingTimer.current);
                                    pendingQueryRef.current = newValue;
                                    typingTimer.current = setTimeout(() => {
                                        if (loading) return; // wait if still loading
                                        runFilter(newValue);
                                    }, 200);
                                }}
                            />
                            {
                                query
                                    && !list.some(item => item.toLowerCase() === query.toLowerCase())
                                    && !selectedItems.some(item => item.toLowerCase() === query.toLowerCase()) ?
                                    <Listbox autoSelection="NONE" onSelect={(value) => {
                                        if (value === 'add-tag') {
                                            const tagToAdd = query;
                                            const nextSelectedItems = selectedItems.includes(tagToAdd) ? selectedItems : [...selectedItems, tagToAdd];

                                            setSelectedItems(nextSelectedItems);
                                            setToAdd(filter(nextSelectedItems, ''));
                                            setAvailable(filter(list.filter(item => !nextSelectedItems.includes(item)), ''));

                                            setQuery('');
                                            props.handleCustomerTagPicker('initialQuery', '');
                                        }
                                    }}>
                                        <Listbox.Action value="add-tag">
                                            <Button icon={PlusCircleIcon} variant="plain">Add "{query}"</Button>
                                        </Listbox.Action>
                                    </Listbox>
                                    :
                                    null
                            }
                        </BlockStack>
                    </Modal.Section>
                </div>
                {
                    loading ?
                        <div className="spinner-small"><Spinner size="small" /> Loading ...</div>
                        :
                        <Modal.Section>
                            {
                                toAdd.length ?
                                    <OptionList
                                        title="Selected"
                                        onChange={(newValue) => {
                                            const removedItem = toAdd.find(x => !newValue.includes(x));
                                            if (!removedItem) return;

                                            const nextToAdd = toAdd.filter(x => x !== removedItem);
                                            const nextSelectedItems = selectedItems.filter(x => x !== removedItem);

                                            setToAdd(nextToAdd);
                                            setSelectedItems(nextSelectedItems);
                                            setAvailable(filter(list.filter(item => !nextToAdd.includes(item)), query));
                                        }}
                                        options={toAdd.map(p => ({ value: p, label: p }))}
                                        selected={toAdd}
                                        allowMultiple
                                    />
                                    : null
                            }
                            {
                                available.length ?
                                    <OptionList
                                        title="Available"
                                        onChange={(newValue) => {
                                            const clickedItem = newValue[0];
                                            if (!clickedItem) return;

                                            setToAdd(prev => prev.includes(clickedItem) ? prev : [...prev, clickedItem]);
                                            setSelectedItems(prev => prev.includes(clickedItem) ? prev : [...prev, clickedItem]);
                                            setAvailable(prev => filter(prev.filter(item => item !== clickedItem), query));
                                        }}
                                        options={available.map(p => ({ value: p, label: p }))}
                                        selected={toAdd}
                                        allowMultiple
                                    />
                                    : null
                            }
                            {
                                !toAdd.length && !available.length ?
                                    <Text alignment="center" tone="subdued">No customer tags found</Text>
                                    : null
                            }
                        </Modal.Section>
                }
            </div>
        </Modal>
    );
}